import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDispatch } from "react-redux"
import { HttpStatusCode } from "axios"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog"
import { Combobox, ETypeOfNotification, InputDate, WindowLoader, getDate, setNotification, useFormValidation } from "medere-ui-framework"
import { BudgetDTO } from "../../dto/budget/BudgetDTO"
import { CompleteBudgetDetailDTO, withDefaultCoveragePercentages } from "../../dto/budget/BudgetDetailDTO"
import { BillablePatient } from "../../dto/budget/BillablePatientDTO"
import { ComboDataDTO } from "../../dto/ComboDataDTO"
import { HealthEntityPlanDTO } from "../../dto/HealthEntityPlanDTO"
import { useEntityService } from "../../servicesHooks/Entity/useEntityService"
import { getAllBillablePatients, getBillingEntities, getBudgetDetailByPatient, saveBudget, updateBudgetPrices } from "../../servicesHooks/BillHospitalization/useBillHospitalizationService"
import UpdateBudgetModal from "./UpdateBudgetModal"
import AddPeriodItemsDialog from "./billHospitalization/AddPeriodItemsDialog"
import BillItemsSection from "./billHospitalization/BillItemsSection"
import BillSummary from "./billHospitalization/BillSummary"
import NvTooltips from "../../components/novarium/NvTooltips"
import { normalizeBudgetItems } from "./billHospitalization/billItems"
import { ContractContext, applyRevaluation, revalueHospitalizationDays, revalueProfessionalActivities } from "./billHospitalization/contractRevaluation"
import { mapDetailsToBudgetItems, splitDetailsByCategory } from "./billHospitalization/detailMapping"
import { baselineWithAutoBillingEntity, useBillingEntityName } from "./billHospitalization/billingEntity"
import './Budget.scss'
import './billHospitalization/novariumBill.scss'
import { parseApiError } from "../../services/apiError"

interface CRUHospitalizationBillViewProps {
    budget: BudgetDTO;
    hideView: () => void;
}

/**
 * Campos obligatorios al guardar. Se valida solo esto: el resto del DTO (surgery, hospitalization, total, validez,
 * campos que manda el backend al editar…) puede venir en -1 o null sin que sea un error, y antes eso bloqueaba
 * el guardado sin marcar ningún campo.
 */
const REQUIRED_FIELDS: (keyof BudgetDTO)[] = ["billingentity", "date", "patient", "agreement", "agreementplan"];

/** Leyenda de cada campo obligatorio cuando falta. */
const REQUIRED_MESSAGES: Partial<Record<keyof BudgetDTO, string>> = {
    billingentity: 'Elegí la entidad facturadora',
    date: 'Completá la fecha de prefacturación',
    patient: 'Elegí un paciente',
    agreement: 'Elegí la obra social',
    agreementplan: 'Elegí el plan',
};

const withDefaultCoveragePercentagesForBudget = (budget: BudgetDTO): BudgetDTO => ({
    ...budget,
    professionalActivities: budget.professionalActivities?.map(withDefaultCoveragePercentages) ?? [],
    disposables: budget.disposables?.map(withDefaultCoveragePercentages) ?? [],
    equipments: budget.equipments?.map(withDefaultCoveragePercentages) ?? [],
    hospitalizationDays: budget.hospitalizationDays?.map(withDefaultCoveragePercentages) ?? [],
    nutritionalSupportItems: budget.nutritionalSupportItems?.map(withDefaultCoveragePercentages) ?? [],
    medicines: budget.medicines?.map(withDefaultCoveragePercentages) ?? [],
    extras: budget.extras?.map(withDefaultCoveragePercentages) ?? [],
});

const contractContextOf = (budget: BudgetDTO): ContractContext => ({
    date: budget.date,
    healthEntityPlan: budget.agreementplan,
    billingEntity: budget.billingentity,
    // Sin internación (paciente solo quirúrgico) llega -1 al crear y null al editar.
    hasHospitalization: Boolean(budget.hospitalization && budget.hospitalization > 0),
});

const hasLoadedItems = (budget: BudgetDTO): boolean =>
    (budget.professionalActivities.length + budget.disposables.length + budget.hospitalizationDays.length +
        budget.nutritionalSupportItems.length + budget.medicines.length + budget.extras.length) > 0;

export default function CRUHospitalizationBillView({ budget, hideView }: CRUHospitalizationBillViewProps) {
    const { getHealthEntityComboDataDTO, getHealthEntityPlanDTO } = useEntityService();

    const dispatch = useDispatch();
    const rootRef = useRef<HTMLDivElement>(null);
    const [CRUBudget, setCRUBudget] = useState<BudgetDTO>(budget);
    const [defaultValuesBudget] = useState<BudgetDTO>(budget);
    const [openUpdateBudgetModal, setOpenUpdateBudgetModal] = useState<boolean>(false);
    const { errors: formErrors, handleValidation, cleanError } = useFormValidation(CRUBudget);
    const [patientDesc, setPatientDesc] = useState<string>(budget.patientdesc ?? '');
    const [loading, setLoading] = useState(false);
    const [revaluing, setRevaluing] = useState(false);
    const [openAddItems, setOpenAddItems] = useState<boolean>(false);

    const isEditing = Boolean(CRUBudget.budget && CRUBudget.budget > 0);
    // El nombre del paciente de una factura guardada viene en addressee (patientdesc puede llegar vacío).
    const patientName = CRUBudget.patientdesc || CRUBudget.addressee || '';
    const hasBillingEntity = Boolean(CRUBudget.billingentity && CRUBudget.billingentity > 0);
    const hasPatient = Boolean(CRUBudget.patient && CRUBudget.patient > 0);
    const hasAgreement = Boolean(CRUBudget.agreement && CRUBudget.agreement > 0 && CRUBudget.agreementdesc);

    const [filterFromDate, setFilterFromDate] = useState<Date>(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 3);
        return date;
    });
    const [filterToDate, setFilterToDate] = useState<Date>(new Date());

    // El AutoComplete de PrimeReact 9.6.5 (base del Combobox) no trae clear: la ✕ se inserta como un ítem más
    // del campo, entre el input y la flecha, para que se vea como la del Dropdown sin depender de medidas.
    // El campo se vuelve a ubicar al cambiar el paciente porque el Combobox remonta el AutoComplete al vaciarse.
    const patientComboRef = useRef<HTMLDivElement>(null);
    const [patientComboField, setPatientComboField] = useState<HTMLElement | null>(null);

    // La factura guardada trae su entidad facturadora: se busca el nombre para mostrarla.
    useBillingEntityName(budget.billingentity, budget.billingentitydesc, (name) =>
        setCRUBudget(prev => prev.billingentity === budget.billingentity ? { ...prev, billingentitydesc: name } : prev));

    useEffect(() => {
        setPatientComboField(patientComboRef.current?.querySelector<HTMLElement>('.p-autocomplete') ?? null);
    }, [patientDesc]);

    const notifyError = (title: string) =>
        dispatch(setNotification({ title, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
    const notifySuccess = (title: string) =>
        dispatch(setNotification({ title, icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));

    const fieldError = (field: keyof BudgetDTO) => Boolean(formErrors[field]);
    const fieldCaption = (field: keyof BudgetDTO) => formErrors[field] ? REQUIRED_MESSAGES[field] : undefined;

    /** Pedido de revalorización vigente: una respuesta vieja (otro paciente, otra entidad) no se aplica. */
    const revaluationRef = useRef(0);

    /**
     * Revaloriza prácticas y estadías con el convenio: al cambiar la entidad facturadora y al traer ítems del
     * período (como antes, solo en las categorías que estaban vacías). `from` = desde qué índice revalorizar;
     * null = esa categoría no se toca.
     */
    async function revalueByContract(base: BudgetDTO, from: { activities: number | null; days: number | null }) {
        if (from.activities === null && from.days === null) return;
        const request = ++revaluationRef.current;
        const context = contractContextOf(base);
        const offset = <T,>(patches: { index: number }[], start: number) => patches.map(p => ({ ...p, index: p.index + start })) as T;
        setRevaluing(true);
        try {
            const [activities, days] = await Promise.all([
                from.activities === null ? Promise.resolve([]) : revalueProfessionalActivities(base.professionalActivities.slice(from.activities), context),
                from.days === null ? Promise.resolve([]) : revalueHospitalizationDays(base.hospitalizationDays.slice(from.days), context),
            ]);
            if (request !== revaluationRef.current) return;
            setCRUBudget(prev => {
                // Si mientras tanto cambió el paciente, la entidad o el plan, el resultado ya no corresponde.
                if (prev.patient !== base.patient || prev.billingentity !== base.billingentity || prev.agreementplan !== base.agreementplan) return prev;
                return {
                    ...prev,
                    professionalActivities: applyRevaluation(prev.professionalActivities, offset(activities, from.activities ?? 0)),
                    hospitalizationDays: applyRevaluation(prev.hospitalizationDays, offset(days, from.days ?? 0)),
                };
            });
        } finally {
            if (request === revaluationRef.current) setRevaluing(false);
        }
    }

    /** Las categorías que estaban vacías antes de una carga se revalorizan desde su primer ítem nuevo. */
    const revaluationScope = (previous: BudgetDTO) => ({
        activities: previous.professionalActivities.length === 0 ? 0 : null,
        days: previous.hospitalizationDays.length === 0 ? 0 : null,
    });

    function save(budgetToSave: BudgetDTO, withValidation: boolean) {
        if (loading || revaluing) return;
        if (withValidation) {
            // En la edición el paciente no se cambia (no se muestra el combo).
            const required = isEditing ? REQUIRED_FIELDS.filter(f => f !== "patient") : REQUIRED_FIELDS;
            const ignoredFields = (Object.keys(budgetToSave) as (keyof BudgetDTO)[]).filter(field => !required.includes(field));
            handleValidation(
                () => save(budgetToSave, false),
                // Se valida desde cero: un error viejo de un campo que ya no se pide no tiene que bloquear.
                {},
                ignoredFields,
                // Guardar no se deshabilita: marca los campos que faltan y lleva la vista al primero.
                () => setTimeout(() => rootRef.current?.querySelector('.p-invalid')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0)
            );
            return;
        }
        setLoading(true);
        saveBudget(normalizeBudgetItems(withDefaultCoveragePercentagesForBudget(budgetToSave)))?.then(res => {
            if (res.status !== HttpStatusCode.Ok) {
                notifyError("No se pudieron guardar los cambios. Intentá de nuevo.");
                return;
            }
            notifySuccess(isEditing ? "Cambios guardados" : "Facturación creada");
            hideView();
        }).catch((error: unknown) => {
            // Un error de negocio (por ejemplo, la factura ya tiene órdenes) dice por qué no se guardó
            const apiError = parseApiError(error);
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notifyError(isBusinessError ? apiError.message : "No se pudieron guardar los cambios. Intentá de nuevo.");
        })
            .finally(() => setLoading(false));
    }

    /** « Volver y Cancelar: si hay cambios, pregunta antes de salir. */
    function requestExit() {
        const baseline = baselineWithAutoBillingEntity(defaultValuesBudget, CRUBudget);
        if (JSON.stringify(CRUBudget) === JSON.stringify(baseline)) {
            hideView();
            return;
        }
        confirmDialog({
            header: '¿Salir sin guardar?',
            message: 'Los cambios que hiciste se van a perder.',
            className: 'nv-scope nv-confirm',
            footer: (options) => (
                <>
                    <Button type="button" link label="Seguir editando" className="nv-push-left" onClick={options.reject} />
                    <Button type="button" className="buttonMain2" outlined label="Salir sin guardar" onClick={options.accept} />
                    <Button type="button" className="buttonMain" label={isEditing ? 'Guardar y salir' : 'Crear y salir'} onClick={() => { options.reject(); save(CRUBudget, true); }} />
                </>
            ),
            accept: hideView,
        });
    }

    function updateBudgetPricesHandler(updateConfirmed: boolean) {
        if (!updateConfirmed || loading) {
            setOpenUpdateBudgetModal(false);
            return;
        }
        setLoading(true);
        updateBudgetPrices(CRUBudget)
            .then((updatedBudget: BudgetDTO) => {
                setCRUBudget(withDefaultCoveragePercentagesForBudget(updatedBudget));
                notifySuccess("Precios actualizados");
            })
            .catch(() => notifyError("No se pudieron actualizar los precios. Revisá el plan e intentá de nuevo."))
            .finally(() => {
                setLoading(false);
                setOpenUpdateBudgetModal(false);
            });
    }

    async function reloadItemsForPeriod() {
        if (!hasPatient) return;
        try {
            setLoading(true);
            const details: CompleteBudgetDetailDTO[] = await getBudgetDetailByPatient(
                CRUBudget.patient, filterFromDate, filterToDate, CRUBudget.billingentity
            );
            if (Array.isArray(details)) {
                const mapped = mapDetailsToBudgetItems(details);
                setCRUBudget(prev => ({ ...prev, ...mapped }));
                void revalueByContract({ ...CRUBudget, ...mapped }, revaluationScope(CRUBudget));
            }
        } catch {
            notifyError("No se pudieron traer los ítems del período. Intentá de nuevo.");
        } finally {
            setLoading(false);
        }
    }

    function handleSearchByDateRange() {
        if (!hasPatient) return;
        if (!hasLoadedItems(CRUBudget)) {
            void reloadItemsForPeriod();
            return;
        }
        confirmDialog({
            header: '¿Actualizar los ítems del período?',
            message: 'Se van a reemplazar los ítems cargados por los del período elegido. Los cambios que no guardaste se pierden.',
            className: 'nv-scope nv-confirm',
            acceptLabel: 'Actualizar',
            acceptClassName: 'buttonMain',
            rejectLabel: 'Cancelar',
            rejectClassName: 'buttonMain2 p-button-outlined',
            accept: () => { void reloadItemsForPeriod(); },
        });
    }

    function handleAddItemsFromPeriod(selectedItems: CompleteBudgetDetailDTO[]) {
        if (!selectedItems || selectedItems.length === 0) return;
        const mapped = mapDetailsToBudgetItems(selectedItems);
        const next: BudgetDTO = {
            ...CRUBudget,
            professionalActivities: [...CRUBudget.professionalActivities, ...mapped.professionalActivities],
            hospitalizationDays: [...CRUBudget.hospitalizationDays, ...mapped.hospitalizationDays],
        };
        void revalueByContract(next, revaluationScope(CRUBudget));
        setCRUBudget(prev => ({
            ...prev,
            professionalActivities: [...prev.professionalActivities, ...mapped.professionalActivities],
            medicines: [...prev.medicines, ...mapped.medicines],
            disposables: [...prev.disposables, ...mapped.disposables],
            nutritionalSupportItems: [...prev.nutritionalSupportItems, ...mapped.nutritionalSupportItems],
            equipments: [...prev.equipments, ...mapped.equipments],
            hospitalizationDays: [...prev.hospitalizationDays, ...mapped.hospitalizationDays],
        }));
        notifySuccess(selectedItems.length === 1 ? "1 ítem agregado" : `${selectedItems.length} ítems agregados`);
    }

    function clearPatient() {
        setPatientDesc('');
        setCRUBudget(prev => ({
            ...defaultValuesBudget,
            billingentity: prev.billingentity,
            billingentitydesc: prev.billingentitydesc,
        }));
    }

    function requestClearPatient() {
        if (!hasLoadedItems(CRUBudget)) {
            clearPatient();
            return;
        }
        confirmDialog({
            header: '¿Limpiar paciente?',
            message: 'Se van a quitar los ítems cargados y los cambios que no guardaste.',
            className: 'nv-scope nv-confirm',
            acceptLabel: 'Limpiar',
            acceptClassName: 'buttonMain',
            rejectLabel: 'Cancelar',
            rejectClassName: 'buttonMain2 p-button-outlined',
            accept: clearPatient,
            // Si no se limpia, el combo vuelve a mostrar el paciente elegido.
            reject: () => setPatientDesc(CRUBudget.patientdesc ?? ''),
        });
    }

    async function handlePatientChange(e: BillablePatient | null) {
        if (!e || (!e.id && !(e.name ?? '').trim())) {
            setPatientDesc('');
            requestClearPatient();
            return;
        }
        setPatientDesc(e.name ?? '');
        // Mientras se tipea llega { name } sin id: el paciente anterior deja de estar elegido (lo marca la
        // validación al guardar) y los ítems se cargan recién al elegir uno de la lista.
        if (!e.id) {
            setCRUBudget(prev => prev.patient > 0 ? { ...prev, patient: -1, patientdesc: '' } : prev);
            return;
        }
        cleanError('patient');
        let items = splitDetailsByCategory([]);
        try {
            setLoading(true);
            const details: CompleteBudgetDetailDTO[] = await getBudgetDetailByPatient(e.id, filterFromDate, filterToDate, CRUBudget.billingentity);
            if (Array.isArray(details)) items = splitDetailsByCategory(details);
        } catch {
            notifyError("No se pudieron traer los ítems del paciente. Intentá de nuevo.");
        } finally {
            setLoading(false);
        }
        const patientFields: Partial<BudgetDTO> = {
            patient: e.id ?? -1,
            patientdesc: e.name ?? '',
            addressee: e.name ?? '',
            dni: e.dni ?? '',
            agreement: e.healthentityid ?? -1,
            agreementdesc: e.healthentityname ?? '',
            affiliatenumber: e.benefitnumber ?? '',
            agreementplan: e.healthentityplanid ?? -1,
            agreementplandesc: e.healthentityplanname ?? '',
            hospitalization: e.hospitalizationid ?? -1,
            surgery: e.surgeryid ?? -1,
            ...items,
        };
        setCRUBudget(prev => ({ ...prev, ...patientFields }));
        // Los ítems son todos del paciente nuevo: se revalorizan completos.
        void revalueByContract({ ...CRUBudget, ...patientFields }, { activities: 0, days: 0 });
    }

    function handleAgreementChange(e: ComboDataDTO | null) {
        // Mientras se tipea llega { name } sin id: la obra social y el plan dejan de estar elegidos.
        if (!e?.id) {
            setCRUBudget(prev => ({ ...prev, agreement: -1, agreementdesc: e?.name ?? '', agreementplan: -1, agreementplandesc: '' }));
            return;
        }
        cleanError('agreement');
        // El combo de planes se recarga con la obra social nueva; si tiene un solo plan, lo elige solo.
        setCRUBudget(prev => ({ ...prev, agreement: e.id ?? -1, agreementdesc: e.name ?? '', agreementplan: -1, agreementplandesc: '' }));
    }

    /** Al elegir un plan se ofrece actualizar los precios de la factura con el convenio. */
    function selectPlan(plan: HealthEntityPlanDTO) {
        cleanError('agreementplan');
        setCRUBudget(prev => ({ ...prev, agreementplan: plan.healthentityplan, agreementplandesc: plan.name }));
        setOpenUpdateBudgetModal(true);
    }

    const itemActions = (
        <>
            {isEditing && hasPatient && (
                <Button
                    type="button"
                    className="buttonMain2"
                    outlined
                    icon="pi pi-plus-circle"
                    label="Agregar ítems del período"
                    onClick={() => setOpenAddItems(true)}
                />
            )}
            {/* En la edición no se reemplazan los ítems: solo se agregan los del período. */}
            {!isEditing && (
                <span className="nv-tip nv-disabled-tip" data-pr-tooltip={hasPatient ? 'Vuelve a traer los ítems del paciente (internación y cirugías) en el período' : 'Elegí un paciente primero'} tabIndex={hasPatient ? undefined : 0}>
                    <Button
                        type="button"
                        className="buttonMain3"
                        icon="pi pi-refresh"
                        label="Actualizar ítems del período"
                        onClick={handleSearchByDateRange}
                        disabled={!hasPatient}
                    />
                </span>
            )}
        </>
    );

    return (
        <div className="nv-scope nv-page" ref={rootRef}>
            <ConfirmDialog />
            <NvTooltips />

            <header className="nv-page-head">
                <Button type="button" link icon="pi pi-angle-double-left" label="Volver" className="nv-back" onClick={requestExit} />
                <h1 className="nv-h1">{isEditing ? 'Editar facturación sanatorial' : 'Nueva facturación sanatorial'}</h1>
                {isEditing && patientName && <span className="nv-subtitle">{patientName}</span>}
            </header>

            <section className="nv-section" aria-labelledby="nv-data-title">
                <h2 className="nv-section-title" id="nv-data-title">Datos de la facturación</h2>
                <div className="nv-form-row">
                    <Combobox
                        label="Entidad facturadora"
                        required
                        className="nv-w-m"
                        getItems={getBillingEntities}
                        // Se elige sola solo si no hay ninguna y hay una sola para elegir; la guardada no se reemplaza.
                        allowsEmptyValues
                        autoselectOnOneItem={!hasBillingEntity}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={CRUBudget.billingentitydesc}
                        defaultValue={defaultValuesBudget?.billingentitydesc}
                        setValue={(e: ComboDataDTO | null) => {
                            if (!e?.id) return;
                            cleanError('billingentity');
                            // Solo se revaloriza si se cambia una entidad por otra, no al elegir la primera.
                            const changed = (CRUBudget.billingentity ?? -1) > 0 && e.id !== CRUBudget.billingentity;
                            const next = { ...CRUBudget, billingentity: e.id, billingentitydesc: e.name ?? '' };
                            setCRUBudget(prev => ({ ...prev, billingentity: e.id ?? -1, billingentitydesc: e.name ?? '' }));
                            // Otra entidad facturadora puede tener otro convenio: se revalorizan prácticas y estadías.
                            if (changed && hasLoadedItems(next)) void revalueByContract(next, { activities: 0, days: 0 });
                        }}
                        error={fieldError('billingentity')}
                        caption={fieldCaption('billingentity')}
                    />
                    <InputDate
                        label="Fecha de prefacturación"
                        required
                        className="nv-w-s"
                        value={getDate(CRUBudget.date)}
                        defaultValue={getDate(defaultValuesBudget?.date)}
                        onChange={(e: { value?: Date | null }) => { cleanError('date'); setCRUBudget(prev => ({ ...prev, date: e.value ?? null })); }}
                        error={fieldError('date')}
                        caption={fieldCaption('date')}
                    />
                </div>
            </section>

            <section className="nv-section" aria-labelledby="nv-patient-title">
                <h2 className="nv-section-title" id="nv-patient-title">Paciente</h2>
                <div className="nv-form-row">
                    {/* El período filtra los pacientes y sus ítems. En la edición se elige en "Agregar ítems del período". */}
                    {!isEditing && (
                        <>
                            <InputDate
                                label="Período desde"
                                className="nv-w-s"
                                value={getDate(filterFromDate)}
                                onChange={(e: { value?: Date | null }) => { if (e.value) setFilterFromDate(e.value); }}
                            />
                            <InputDate
                                label="Período hasta"
                                className="nv-w-s"
                                value={getDate(filterToDate)}
                                onChange={(e: { value?: Date | null }) => { if (e.value) setFilterToDate(e.value); }}
                            />
                        </>
                    )}
                    {!isEditing && (
                        <div ref={patientComboRef} className="patient-combo">
                            <Combobox
                                label="Paciente"
                                required
                                className="nv-w-m"
                                getItems={getAllBillablePatients}
                                params={{ fromDate: filterFromDate, toDate: filterToDate }}
                                reLoadItemsValue={`${filterFromDate?.getTime()}-${filterToDate?.getTime()}`}
                                optionLabel="name"
                                itemTemplate={(item: BillablePatient) => (
                                    <span>{item.name}{item.dni ? ` (DNI ${item.dni})` : ''}</span>
                                )}
                                placeholder={hasBillingEntity ? "Buscá por nombre o DNI" : "Elegí la entidad facturadora primero"}
                                disable={!hasBillingEntity}
                                value={patientDesc}
                                forceSelection={false}
                                setValue={handlePatientChange}
                                error={fieldError('patient')}
                                caption={fieldCaption('patient')}
                            />
                            {patientComboField && patientDesc && createPortal(
                                <Button
                                    type="button"
                                    icon="pi pi-times"
                                    text
                                    className="patient-combo-clear nv-tip"
                                    aria-label="Limpiar paciente"
                                    data-pr-tooltip="Limpiar paciente"
                                    onClick={() => handlePatientChange(null)}
                                />,
                                patientComboField
                            )}
                        </div>
                    )}
                    {/* En la edición el paciente no se cambia: se muestra como dato. */}
                    {isEditing && (
                        <div className="nv-ro nv-ro-aligned nv-w-m">
                            <span className="nv-ro-label">Paciente</span>
                            <span className="nv-ro-value nv-truncate">{patientName || '—'}</span>
                        </div>
                    )}
                </div>
                <div className="nv-form-row">
                    {/* Datos personales del paciente: vienen del paciente y no se editan acá. */}
                    <div className="nv-ro nv-ro-aligned nv-w-s">
                        <span className="nv-ro-label">DNI</span>
                        <span className="nv-ro-value">{CRUBudget.dni || '—'}</span>
                    </div>
                    <div className="nv-field nv-w-s">
                        <label htmlFor="nv-affiliate">N.° de afiliado</label>
                        <InputText
                            id="nv-affiliate"
                            value={CRUBudget.affiliatenumber ?? ''}
                            onChange={(e) => setCRUBudget(prev => ({ ...prev, affiliatenumber: e.target.value }))}
                            className={hasPatient ? 'nv-autofilled' : undefined}
                        />
                    </div>
                    <Combobox
                        label="Obra social"
                        required
                        className={`nv-w-m ${hasPatient ? 'nv-autofilled' : ''}`}
                        getItems={getHealthEntityComboDataDTO}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={CRUBudget.agreementdesc}
                        defaultValue={defaultValuesBudget?.agreementdesc}
                        forceSelection={false}
                        setValue={handleAgreementChange}
                        error={fieldError('agreement')}
                        caption={fieldCaption('agreement')}
                    />
                    <Combobox
                        label="Plan"
                        required
                        className={`nv-w-m ${hasPatient ? 'nv-autofilled' : ''}`}
                        getItems={getHealthEntityPlanDTO}
                        params={{ healthentity: CRUBudget.agreement }}
                        optionLabel="name"
                        placeholder={hasAgreement ? "Seleccionar" : "Elegí una obra social primero"}
                        disable={!hasAgreement}
                        value={CRUBudget.agreementplandesc || ""}
                        defaultValue={defaultValuesBudget?.agreementplandesc || ""}
                        forceSelection={false}
                        setValue={(e: HealthEntityPlanDTO | null) => {
                            if (e?.healthentityplan) selectPlan(e);
                            // Mientras se tipea, el plan deja de estar elegido.
                            else if (e) setCRUBudget(prev => ({ ...prev, agreementplan: -1, agreementplandesc: e.name ?? '' }));
                        }}
                        reLoadItemsValue={CRUBudget.agreement}
                        error={fieldError('agreementplan')}
                        caption={fieldCaption('agreementplan')}
                    />
                </div>
            </section>

            <BillItemsSection
                budget={CRUBudget}
                setBudget={setCRUBudget}
                contract={contractContextOf(CRUBudget)}
                healthEntity={CRUBudget.agreement}
                hospitalizationId={CRUBudget.hospitalization}
                actions={itemActions}
            />

            <BillSummary budget={CRUBudget} />

            <footer className="nv-page-foot">
                <hr className="nv-divider" />
                <div className="nv-actions-end">
                    <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={requestExit} />
                    <Button type="button" className="buttonMain" label={isEditing ? "Guardar cambios" : "Crear"} onClick={() => save(CRUBudget, true)} />
                </div>
            </footer>

            {/* TODO(design-system): UpdateBudgetModal todavía usa el estilo anterior. */}
            <UpdateBudgetModal
                openUpdateBudgetModal={openUpdateBudgetModal}
                setOpenUpdateBudgetModal={setOpenUpdateBudgetModal}
                updateBudget={updateBudgetPricesHandler}
                editMode={CRUBudget.budget != null}
            />
            {isEditing && hasPatient && (
                <AddPeriodItemsDialog
                    visible={openAddItems}
                    onHide={() => setOpenAddItems(false)}
                    patient={CRUBudget.patient}
                    billingEntity={CRUBudget.billingentity}
                    initialFrom={filterFromDate}
                    initialTo={filterToDate}
                    onAdd={handleAddItemsFromPeriod}
                />
            )}
            <WindowLoader showLoader={loading || revaluing} className='whiteSpinner' />
        </div>
    );
}
